"use client";
import { useEffect, useState } from "react";
import { Heart } from "lucide-react";
import { Button } from "../../shadcn/button";
import { cn } from "@/app/utils/cn";
import { useToggleFavorite } from "@/app/lib/features/favorite/favorite.hook";
import { toast } from "sonner";
import { trackProductFavorite } from "@/app/lib/tracking/interactions";

interface FavoriteButtonProps {
  productId: number;
  /** Favori bu varyant için işlenir — kartın gösterdiği rengin variant_id'si. */
  variantId?: number;
  className?: string;
  /** Kartın gösterdiği varyantın API'den gelen is_favorited değeri. */
  initialIsFavorited?: boolean;
}

const FavoriteButton = ({
  productId,
  variantId,
  className,
  initialIsFavorited,
}: FavoriteButtonProps) => {
  // Dispatch-only: durum aşağıdaki local state'te, slice'tan okuma yok.
  const toggleFavorite = useToggleFavorite();

  // Durum API verisinden gelir: variants_by_color[].is_favorited. Favori
  // listesine/ürün seviyesindeki bayrağa bakılmaz — aynı ürünün başka bir rengi
  // favorideyse bu kartın kalbi dolmasın diye.
  const [favorited, setFavorited] = useState(!!initialIsFavorited);
  const [loading, setLoading] = useState(false);

  // Kart yeni veriyle (ör. yeniden fetch, farklı varyant) render edilirse eşitle.
  useEffect(() => {
    setFavorited(!!initialIsFavorited);
  }, [initialIsFavorited, variantId]);

  const handleToggle = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (loading) return;

    const wasFavorited = favorited;
    // İyimser güncelleme — istek dönene kadar kalp bekletilmesin.
    setFavorited(!wasFavorited);
    setLoading(true);

    const success = await toggleFavorite(productId, variantId);
    setLoading(false);

    if (success) {
      if (!wasFavorited) trackProductFavorite(productId);
      toast.success(
        wasFavorited ? "Favorilerden çıkarıldı" : "Favorilere eklendi",
      );
    } else {
      setFavorited(wasFavorited);
      toast.error("İşlem başarısız oldu");
    }
  };

  return (
    <Button
      size="icon-sm"
      variant="outline"
      className={cn("rounded-full shadow-sm", className)}
      onClick={handleToggle}
      disabled={loading}
      aria-label={favorited ? "Favorilerden çıkar" : "Favorilere ekle"}
    >
      <Heart
        size={19}
        className={cn(
          "transition-colors",
          favorited && "fill-red-500 text-red-500",
        )}
      />
    </Button>
  );
};

export default FavoriteButton;
